/**
 * Universal Search Engine for BrewBase
 *
 * This module provides an extensible search system that can handle:
 * - Fuzzy matching across multiple fields
 * - Command-based quick actions
 * - AI integration (future)
 * - Customizable ranking algorithms
 */

import { type RouterOutputs } from "@/hooks/trpc";
import type { LucideIcon } from "lucide-react";

export type SearchableItem = RouterOutputs["search"]["getAllItems"][number];

export interface SearchCommand {
  id: string;
  trigger: string;
  title: string;
  description: string;
  keywords: string[];
  category: "create" | "navigate" | "action";
  action: () => void | Promise<void>;
  icon?: LucideIcon;
}

export interface SearchResult {
  item?: SearchableItem;
  command?: SearchCommand;
  score: number;
  matchedFields: string[];
  highlightRanges: Array<{ start: number; end: number; field: string }>;
}

export interface SearchOptions {
  includeCommands?: boolean;
  maxResults?: number;
  threshold?: number;
  weights?: {
    title: number;
    subtitle: number;
    description: number;
    metadata: number;
    exactMatch: number;
    prefixMatch: number;
    fuzzyMatch: number;
  };
}

class SearchEngine {
  private items: SearchableItem[] = [];
  private commands: SearchCommand[] = [];

  private defaultOptions: Required<SearchOptions> = {
    includeCommands: true,
    maxResults: 10,
    threshold: 0.1,
    weights: {
      title: 3.0,
      subtitle: 2.0,
      description: 1.5,
      metadata: 1.0,
      exactMatch: 5.0,
      prefixMatch: 3.0,
      fuzzyMatch: 1.0,
    },
  };

  constructor() {
    this.registerDefaultCommands();
  }

  /**
   * Update the searchable items
   */
  setItems(items: SearchableItem[]): void {
    this.items = items;
  }

  /**
   * Register a new command
   */
  registerCommand(command: SearchCommand): void {
    this.commands.push(command);
  }

  /**
   * Register multiple commands
   */
  registerCommands(commands: SearchCommand[]): void {
    this.commands.push(...commands);
  }

  /**
   * Set commands (replace existing)
   */
  setCommands(commands: SearchCommand[]): void {
    this.commands = [...commands];
  }

  /**
   * Main search function
   */
  search(query: string, options: SearchOptions = {}): SearchResult[] {
    const opts = { ...this.defaultOptions, ...options };
    const normalizedQuery = this.normalizeQuery(query);

    if (!normalizedQuery) {
      return this.getDefaultResults(opts);
    }

    const results: SearchResult[] = [];

    // Search items
    for (const item of this.items) {
      const result = this.searchItem(item, normalizedQuery, opts);
      if (result.score >= opts.threshold) {
        results.push(result);
      }
    }

    // Search commands
    if (opts.includeCommands) {
      for (const command of this.commands) {
        const result = this.searchCommand(command, normalizedQuery, opts);
        if (result.score >= opts.threshold) {
          results.push(result);
        }
      }
    }

    // Sort by score and limit results
    return results.sort((a, b) => b.score - a.score).slice(0, opts.maxResults);
  }

  private normalizeQuery(query: string): string {
    return query.trim().toLowerCase();
  }

  /**
   * Normalize text for more flexible matching
   * Removes common separators and special characters for better matching
   */
  private normalizeText(text: string): string {
    return text
      .toLowerCase()
      .replace(/[-_\s\.]/g, "") // Remove hyphens, underscores, spaces, dots
      .replace(/[^\w]/g, ""); // Remove any remaining non-word characters
  }

  /**
   * Check if query is a subsequence of text (all characters appear in order)
   * Example: "p18" is a subsequence of "p018"
   */
  private isSubsequence(text: string, query: string): boolean {
    let textIndex = 0;
    let queryIndex = 0;

    while (textIndex < text.length && queryIndex < query.length) {
      if (text[textIndex] === query[queryIndex]) {
        queryIndex++;
      }
      textIndex++;
    }

    return queryIndex === query.length;
  }

  private getDefaultResults(opts: Required<SearchOptions>): SearchResult[] {
    // Return recent/popular items when no query
    return this.items.slice(0, Math.min(5, opts.maxResults)).map((item) => ({
      item,
      score: 0.5,
      matchedFields: [],
      highlightRanges: [],
    }));
  }

  private searchItem(
    item: SearchableItem,
    query: string,
    opts: Required<SearchOptions>,
  ): SearchResult {
    const searchableText = this.getSearchableText(item);
    const score = this.calculateScore(searchableText, query, opts.weights);

    return {
      item,
      score,
      matchedFields: this.getMatchedFields(item, query),
      highlightRanges: this.getHighlightRanges(searchableText, query),
    };
  }

  private searchCommand(
    command: SearchCommand,
    query: string,
    opts: Required<SearchOptions>,
  ): SearchResult {
    const searchableText = {
      title: command.title,
      subtitle: "",
      description: command.description,
      keywords: command.keywords.join(" "),
    };

    const score = this.calculateScore(searchableText, query, opts.weights);

    return {
      command,
      score,
      matchedFields: this.getMatchedFields(
        { title: command.title, description: command.description },
        query,
      ),
      highlightRanges: this.getHighlightRanges(searchableText, query),
    };
  }

  private getSearchableText(item: SearchableItem): Record<string, string> {
    return {
      title: item.title || "",
      subtitle: item.subtitle || "",
      description: item.description || "",
      metadata: Object.values(item.metadata || {})
        .filter(Boolean)
        .join(" "),
    };
  }

  private calculateScore(
    searchableText: Record<string, string>,
    query: string,
    weights: Required<SearchOptions>["weights"],
  ): number {
    let totalScore = 0;
    let maxPossibleScore = 0;

    const normalizedQuery = this.normalizeText(query);

    for (const [field, text] of Object.entries(searchableText)) {
      if (!text) continue;

      const fieldWeight = weights[field as keyof typeof weights] || 1.0;
      maxPossibleScore += fieldWeight * weights.exactMatch * 1.2; // Account for perfect match bonus

      const normalizedText = text.toLowerCase();
      const fullyNormalizedText = this.normalizeText(text);

      // Perfect exact match (case-insensitive)
      if (normalizedText === query) {
        totalScore += fieldWeight * weights.exactMatch * 1.2; // Boost for perfect match
        continue;
      }

      // Perfect normalized match (e.g., "p018" matches "P-018")
      if (fullyNormalizedText === normalizedQuery) {
        totalScore += fieldWeight * weights.exactMatch * 1.1; // High boost for normalized exact match
        continue;
      }

      // Contains match (case-insensitive)
      if (normalizedText.includes(query)) {
        totalScore += fieldWeight * weights.exactMatch;
        continue;
      }

      // Normalized contains match, but with length penalty for longer text
      if (fullyNormalizedText.includes(normalizedQuery)) {
        const lengthPenalty =
          normalizedQuery.length / fullyNormalizedText.length;
        totalScore += fieldWeight * weights.exactMatch * 0.9 * lengthPenalty;
        continue;
      }

      // Special case: check if query is a subsequence (all characters appear in order)
      // This handles cases like "p18" matching "p018"
      if (this.isSubsequence(fullyNormalizedText, normalizedQuery)) {
        const matchRatio = normalizedQuery.length / fullyNormalizedText.length;
        totalScore += fieldWeight * weights.exactMatch * 0.7 * matchRatio;
        continue;
      }

      // Prefix match
      if (normalizedText.startsWith(query)) {
        totalScore += fieldWeight * weights.prefixMatch;
        continue;
      }

      // Normalized prefix match
      if (fullyNormalizedText.startsWith(normalizedQuery)) {
        totalScore += fieldWeight * weights.prefixMatch * 0.9;
        continue;
      }

      // Fuzzy match on original text
      const fuzzyScore = this.fuzzyMatch(normalizedText, query);
      if (fuzzyScore > 0) {
        totalScore += fieldWeight * weights.fuzzyMatch * fuzzyScore;
        continue;
      }

      // Fuzzy match on normalized text
      const normalizedFuzzyScore = this.fuzzyMatch(
        fullyNormalizedText,
        normalizedQuery,
      );
      if (normalizedFuzzyScore > 0) {
        totalScore +=
          fieldWeight * weights.fuzzyMatch * normalizedFuzzyScore * 0.8;
      }
    }

    return maxPossibleScore > 0 ? totalScore / maxPossibleScore : 0;
  }

  private fuzzyMatch(text: string, query: string): number {
    // Simple fuzzy matching - can be enhanced with more sophisticated algorithms
    let score = 0;
    let queryIndex = 0;
    let consecutiveMatches = 0;
    let maxConsecutive = 0;

    for (let i = 0; i < text.length && queryIndex < query.length; i++) {
      if (text[i] === query[queryIndex]) {
        score++;
        queryIndex++;
        consecutiveMatches++;
        maxConsecutive = Math.max(maxConsecutive, consecutiveMatches);
      } else {
        consecutiveMatches = 0;
      }
    }

    if (queryIndex !== query.length) {
      return 0;
    }

    // Boost score for consecutive character matches
    const consecutiveBonus = maxConsecutive / query.length;
    return (score / query.length) * (0.5 + 0.5 * consecutiveBonus);
  }

  private getMatchedFields(item: any, query: string): string[] {
    const matched: string[] = [];
    const fields = ["title", "subtitle", "description"];
    const normalizedQuery = this.normalizeText(query);

    for (const field of fields) {
      const value = item[field];
      if (value) {
        const normalizedValue = value.toLowerCase();
        const fullyNormalizedValue = this.normalizeText(value);

        // Check direct, normalized, and subsequence matching
        if (
          normalizedValue.includes(query) ||
          fullyNormalizedValue.includes(normalizedQuery) ||
          this.isSubsequence(fullyNormalizedValue, normalizedQuery)
        ) {
          matched.push(field);
        }
      }
    }

    return matched;
  }

  private getHighlightRanges(
    searchableText: Record<string, string>,
    query: string,
  ): Array<{ start: number; end: number; field: string }> {
    const ranges: Array<{ start: number; end: number; field: string }> = [];

    for (const [field, text] of Object.entries(searchableText)) {
      if (!text) continue;

      const normalizedText = text.toLowerCase();
      let startIndex = 0;

      // Try direct matching for highlighting
      while (true) {
        const index = normalizedText.indexOf(query, startIndex);
        if (index === -1) break;

        ranges.push({
          start: index,
          end: index + query.length,
          field,
        });

        startIndex = index + 1;
      }

      // If no direct matches found, try simple pattern matching without hyphens
      if (ranges.filter((r) => r.field === field).length === 0) {
        const queryWithoutHyphens = query.replace(/[-_\s\.]/g, "");
        const textWithoutHyphens = text.replace(/[-_\s\.]/g, "").toLowerCase();

        if (
          queryWithoutHyphens &&
          textWithoutHyphens.includes(queryWithoutHyphens)
        ) {
          // For simplified highlighting, just highlight the whole matched part
          // This isn't perfect but handles the common case of "p018" vs "P-018"
          const originalMatch = new RegExp(
            query
              .split("")
              .map((ch) => ch.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"))
              .join("[-_\\s\\.]*"),
            "i",
          );
          const match = originalMatch.exec(text);
          if (match) {
            ranges.push({
              start: match.index,
              end: match.index + match[0].length,
              field,
            });
          }
        }
      }
    }

    return ranges;
  }

  private registerDefaultCommands(): void {
    // These will be populated by the UI components
    this.commands = [];
  }
}

// Singleton instance
export const searchEngine = new SearchEngine();

// Helper function to highlight text
export function highlightText(
  text: string,
  highlights: Array<{ start: number; end: number }>,
  className: string = "bg-primary/20",
): React.ReactNode {
  if (!highlights.length) return text;

  const parts: React.ReactNode[] = [];
  let lastIndex = 0;

  highlights
    .sort((a, b) => a.start - b.start)
    .forEach((highlight, index) => {
      // Add text before highlight
      if (highlight.start > lastIndex) {
        parts.push(text.slice(lastIndex, highlight.start));
      }

      // Add highlighted text
      parts.push(
        <span key={index} className={className}>
          {text.slice(highlight.start, highlight.end)}
        </span>,
      );

      lastIndex = highlight.end;
    });

  // Add remaining text
  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }

  return parts;
}
